import { formatNumber, useLocale, useTranslation } from "@ncp/i18n";
import { Badge, Tooltip } from "@ncp/ui";
import { riskLevelOf, useCalculationMethod } from "@/features/case/figures";

/**
 * The risk of a file on one scale: its level from the score bands of the published calculation method, with the score itself in the tooltip;
 * unscored files say so. The bands are read from the method, never held here. Given the file's type, the tooltip names the measure the score
 * is (condition 2.20): the structural risk score of a concentration, or the risk score of a file outside merger control – two calculations,
 * each published under its own name.
 */
export function RiskLevel({ score, type }: { score: number | null | undefined; type?: string }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const method = useCalculationMethod();
  if (score == null) return <span className="text-xs text-muted-foreground">{t("backoffice:risk.notAssessed")}</span>;
  const bands = method.data?.bands;
  const level = bands ? riskLevelOf(score, bands) : null;
  const measure = type ? method.data?.measures.find((m) => m.key === (type === "EconomicConcentration" ? "riskScore" : "fileRiskScore")) : undefined;
  const value = formatNumber(score, locale);
  return (
    <Tooltip content={measure ? t("backoffice:risk.namedScore", { name: locale === "ar" ? measure.nameAr : measure.nameEn, score: value }) : t("backoffice:risk.score", { score: value })}>
      <Badge variant={level === "High" ? "danger" : level === "Medium" ? "warning" : level === "Low" ? "success" : "outline"} size="sm" tabIndex={0}>{level ? t(`backoffice:risk.level.${level}`) : formatNumber(score, locale)}</Badge>
    </Tooltip>
  );
}
